import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Profil, Profilfassung } from '../api';
import { IconOk } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, EmptyState, ErrorState, LoadingState, StatusBadge,
} from '../muster';

/**
 * Die Exportprofile — PDF, DWG, IFC.
 *
 * --- Die Schluessel sind camelCase -------------------------------------------
 *
 * Die API liefert die Definition ueber ASP.NET, und das schreibt camelCase: 'pdf',
 * 'dwg', 'sheetSetName'. Der Katalog muss dieselbe Schreibweise verwenden.
 *
 * Zuerst stand hier PascalCase - wie in der C#-Klasse. Die Folge war schlimmer als
 * eine leere Maske: Beim Speichern entstanden NEUE Abschnitte 'Pdf' und 'Dwg' neben
 * den vorhandenen, und beim Einlesen gewann der neue. PDF und DWG standen danach auf
 * false, und drei Fassungen lang hat es niemand bemerkt.
 *
 * --- Warum feldkataloggetrieben ---------------------------------------------
 *
 * Die drei Formate haben zusammen 76 Einstellungen, und es werden mehr. Eine Maske,
 * die jedes Feld einzeln ausschreibt, ist beim nächsten Feld veraltet — und
 * niemand merkt es, weil das Feld dann einfach fehlt.
 *
 * Deshalb ein Katalog: Jedes Feld eine Zeile mit Schlüssel, Beschriftung, Art und
 * Hinweis. Die Darstellung entsteht daraus. Ein neues Feld ist eine Zeile hier,
 * nicht ein neues Formular.
 *
 * --- Warum immer eine neue Fassung ------------------------------------------
 *
 * Bestehende Fassungen werden nie geändert. Ein Auftrag von vor drei Monaten muss
 * nachvollziehbar bleiben — mit genau den Einstellungen, mit denen er lief. Die API
 * erzwingt das ohnehin: Sie legt an, ändert nicht.
 */

// --- Der Feldkatalog --------------------------------------------------------

type Feldart = 'text' | 'zahl' | 'kommazahl' | 'ja_nein' | 'auswahl' | 'liste';

interface Feld {
  schluessel: string;
  bezeichnung: string;
  art: Feldart;
  auswahl?: string[];
  hinweis?: string;
  /** Feld hervorheben — es entscheidet über Erfolg oder Fehlschlag. */
  wichtig?: boolean;
}

interface Abschnitt {
  titel: string;
  felder: Feld[];
}

const PLANWAHL: Feld[] = [
  {
    schluessel: 'sheetSetName',
    bezeichnung: 'Blattsatz',
    art: 'text',
    wichtig: true,
    hinweis:
      'Der Name eines in Revit gespeicherten Blattsatzes — nicht der '
      + 'Exporteinrichtung! Im Revit-Dialog stehen beide untereinander. Mehrere '
      + 'Namen sind zulässig, durch Senkrechtstrich getrennt und in dieser '
      + 'Reihenfolge probiert: TUV_DWG_Export|TUV_PDF_Export',
  },
  { schluessel: 'sheetParameter', bezeichnung: 'Planparameter (Rückfall)', art: 'text',
    hinweis: 'Nur wenn kein Blattsatz gepflegt wird.' },
  { schluessel: 'sheetParameterValue', bezeichnung: 'Wert des Planparameters', art: 'text' },
  { schluessel: 'sheetNumbers', bezeichnung: 'Einzelne Plannummern', art: 'liste',
    hinweis: 'Komma getrennt. Nur für Sonderfälle.' },
];

const ABSCHNITTE: Record<string, Abschnitt[]> = {
  pdf: [
    { titel: 'Planauswahl', felder: PLANWAHL },
    {
      titel: 'Ausgabe',
      felder: [
        { schluessel: 'combine', bezeichnung: 'Zu einer Datei zusammenfassen', art: 'ja_nein' },
        { schluessel: 'colorDepth', bezeichnung: 'Farbtiefe', art: 'auswahl',
          auswahl: ['COLOR', 'GRAYSCALE', 'BLACK_LINE'] },
        { schluessel: 'rasterQuality', bezeichnung: 'Rasterqualität', art: 'auswahl',
          auswahl: ['LOW', 'MEDIUM', 'HIGH', 'PRESENTATION'] },
        { schluessel: 'exportQualityDpi', bezeichnung: 'Auflösung', art: 'zahl',
          hinweis: 'dpi. 600 ist für Planausgaben üblich.' },
        { schluessel: 'paperFormat', bezeichnung: 'Papierformat', art: 'auswahl',
          auswahl: ['DEFAULT', 'A0', 'A1', 'A2', 'A3', 'A4'] },
        { schluessel: 'paperPlacement', bezeichnung: 'Lage auf dem Blatt', art: 'auswahl',
          auswahl: ['CENTER', 'OFFSET_FROM_CORNER'] },
        { schluessel: 'paperOrientation', bezeichnung: 'Ausrichtung', art: 'auswahl',
          auswahl: ['AUTO', 'PORTRAIT', 'LANDSCAPE'] },
        { schluessel: 'zoomPercentage', bezeichnung: 'Maßstab', art: 'zahl', hinweis: 'Prozent.' },
      ],
    },
    {
      titel: 'Was ausgeblendet wird',
      felder: [
        { schluessel: 'hideCropBoundaries', bezeichnung: 'Zuschneidebereiche', art: 'ja_nein' },
        { schluessel: 'hideScopeBoxes', bezeichnung: 'Bereichsrahmen', art: 'ja_nein' },
        { schluessel: 'hideRefWorkPlanes', bezeichnung: 'Referenz- und Arbeitsebenen', art: 'ja_nein' },
        { schluessel: 'hideUnreferencedViewTags', bezeichnung: 'Unreferenzierte Ansichtssymbole', art: 'ja_nein' },
      ],
    },
    {
      titel: 'Darstellung',
      felder: [
        { schluessel: 'maskCoincidentLines', bezeichnung: 'Deckungsgleiche Linien maskieren', art: 'ja_nein' },
        { schluessel: 'viewLinksInBlue', bezeichnung: 'Ansichtsverweise blau', art: 'ja_nein' },
        { schluessel: 'replaceHalftoneWithThinLines', bezeichnung: 'Halbton durch dünne Linien', art: 'ja_nein' },
        { schluessel: 'alwaysUseRaster', bezeichnung: 'Immer rastern', art: 'ja_nein',
          hinweis: 'Erzeugt große Dateien. Nur wenn Schraffuren sonst falsch kommen.' },
      ],
    },
    {
      titel: 'Ablauf',
      felder: [
        { schluessel: 'stopOnError', bezeichnung: 'Bei Fehler abbrechen', art: 'ja_nein' },
        { schluessel: 'namingPattern', bezeichnung: 'Dateiname', art: 'text',
          hinweis: 'Platzhalter: {Project} {Model} {SheetNumber} {SheetName} {Revision} {Date}' },
      ],
    },
  ],

  dwg: [
    { titel: 'Planauswahl', felder: PLANWAHL },
    {
      titel: 'Exporteinrichtung',
      felder: [
        {
          schluessel: 'exportSetupName',
          bezeichnung: 'Exporteinrichtung im Modell',
          art: 'text',
          wichtig: true,
          hinweis:
            'Die in Revit gespeicherte Einrichtung, etwa TUV_DWG_Export. Sie trägt '
            + 'die Layerzuordnung. Bleibt sie leer, baut der Export die Optionen '
            + 'selbst zusammen — und die im Modell gepflegte Zuordnung bleibt '
            + 'wirkungslos, ohne dass es auffällt.',
        },
        { schluessel: 'layerMappingFileId', bezeichnung: 'Layerzuordnungsdatei', art: 'text',
          hinweis: 'Nur wenn keine Exporteinrichtung genutzt wird.' },
        { schluessel: 'lineTypeMappingFileId', bezeichnung: 'Linientypzuordnung', art: 'text' },
        { schluessel: 'hatchPatternFileId', bezeichnung: 'Schraffurzuordnung', art: 'text' },
      ],
    },
    {
      titel: 'Format',
      felder: [
        { schluessel: 'fileVersion', bezeichnung: 'DWG-Fassung', art: 'auswahl',
          auswahl: ['R2000', 'R2004', 'R2007', 'R2010', 'R2013', 'R2018'] },
        { schluessel: 'coordinates', bezeichnung: 'Koordinaten', art: 'auswahl',
          auswahl: ['SHARED', 'PROJECT_INTERNAL'],
          hinweis: 'SHARED = Vermessungspunkt. Für die Weitergabe fast immer richtig.' },
        { schluessel: 'targetUnit', bezeichnung: 'Einheit', art: 'auswahl',
          auswahl: ['MILLIMETER', 'CENTIMETER', 'METER', 'INCH', 'FOOT'] },
        { schluessel: 'exportLinks', bezeichnung: 'Verknüpfungen', art: 'auswahl',
          auswahl: ['XREF', 'MERGE', 'NONE'] },
        { schluessel: 'solidGeometry', bezeichnung: 'Körpergeometrie', art: 'auswahl',
          auswahl: ['POLYMESH', 'ACIS_SOLIDS'] },
        { schluessel: 'lineScaling', bezeichnung: 'Linienskalierung', art: 'auswahl',
          auswahl: ['PAPER_SPACE', 'MODEL_SPACE'] },
        { schluessel: 'mergedViews', bezeichnung: 'Ansichten zusammenführen', art: 'ja_nein' },
        { schluessel: 'textTreatment', bezeichnung: 'Textbehandlung', art: 'auswahl',
          auswahl: ['EXACT', 'APPROXIMATE'],
          hinweis: 'Wird derzeit NICHT ausgewertet — in der Revit-2025-Schnittstelle '
                 + 'gibt es dafür keinen eigenen Typ. Das Feld bleibt sichtbar, damit '
                 + 'ein gepflegter Wert nicht stillschweigend verschwindet.' },
      ],
    },
    {
      titel: 'Was ausgeblendet wird',
      felder: [
        { schluessel: 'hideScopeBoxes', bezeichnung: 'Bereichsrahmen', art: 'ja_nein' },
        { schluessel: 'hideReferencePlanes', bezeichnung: 'Referenzebenen', art: 'ja_nein' },
        { schluessel: 'hideUnreferencedViewTags', bezeichnung: 'Unreferenzierte Ansichtssymbole', art: 'ja_nein' },
        { schluessel: 'preserveCoincidentLines', bezeichnung: 'Deckungsgleiche Linien erhalten', art: 'ja_nein' },
        { schluessel: 'exportRoomsAsPolylines', bezeichnung: 'Räume als Polylinien', art: 'ja_nein' },
      ],
    },
    {
      titel: 'Ablauf',
      felder: [
        { schluessel: 'namingPattern', bezeichnung: 'Dateiname', art: 'text' },
      ],
    },
  ],

  ifc: [
    {
      titel: 'Schema und Umfang',
      felder: [
        { schluessel: 'schema', bezeichnung: 'IFC-Schema', art: 'auswahl', wichtig: true,
          auswahl: ['IFC2X3_COORDINATION_VIEW_2', 'IFC2X3_TC1', 'IFC4_REFERENCE_VIEW',
                    'IFC4_DESIGN_TRANSFER_VIEW'],
          hinweis: 'Was der Empfänger verlangt. IFC4 Reference View ist der Regelfall.' },
        { schluessel: 'fileType', bezeichnung: 'Dateiart', art: 'auswahl',
          auswahl: ['IFC', 'IFCXML', 'IFCZIP'] },
        { schluessel: 'exportSetupName', bezeichnung: 'Exporteinrichtung im Modell', art: 'text',
          hinweis: 'Wie bei DWG: die in Revit gespeicherte Einrichtung.' },
        { schluessel: 'filterViewName', bezeichnung: 'Filteransicht', art: 'text' },
        { schluessel: 'phaseName', bezeichnung: 'Phase', art: 'text' },
      ],
    },
    {
      titel: 'Lage',
      felder: [
        { schluessel: 'sitePlacement', bezeichnung: 'Bezugspunkt', art: 'auswahl',
          auswahl: ['SHARED', 'SITE', 'PROJECT', 'INTERNAL'],
          hinweis: 'Anders als bei DWG lässt sich das in der Revit-API nicht immer '
                 + 'zuverlässig setzen — das Protokoll warnt gegebenenfalls.' },
        { schluessel: 'includeSiteElevation', bezeichnung: 'Geländehöhe einbeziehen', art: 'ja_nein' },
      ],
    },
    {
      titel: 'Eigenschaften',
      felder: [
        { schluessel: 'exportBaseQuantities', bezeichnung: 'Basismengen', art: 'ja_nein' },
        { schluessel: 'exportCommonPropertySets', bezeichnung: 'Allgemeine Eigenschaftssätze', art: 'ja_nein' },
        { schluessel: 'exportInternalRevitPropertySets', bezeichnung: 'Revit-eigene Eigenschaften', art: 'ja_nein' },
        { schluessel: 'exportUserDefinedPropertySets', bezeichnung: 'Eigene Eigenschaftssätze', art: 'ja_nein' },
        { schluessel: 'exportScheduleAsPsets', bezeichnung: 'Bauteillisten als Eigenschaftssätze', art: 'ja_nein' },
        { schluessel: 'propertySetFileId', bezeichnung: 'Datei der Eigenschaftssätze', art: 'text' },
        { schluessel: 'parameterMappingFileId', bezeichnung: 'Parameterzuordnung', art: 'text' },
        { schluessel: 'classificationFileId', bezeichnung: 'Klassifizierung', art: 'text' },
        { schluessel: 'storeIfcGuid', bezeichnung: 'IFC-GUID im Modell ablegen', art: 'ja_nein',
          hinweis: 'Schreibt zurück ins Modell. Nötig, damit die GUID über Läufe hinweg gleich bleibt.' },
        { schluessel: 'useFamilyAndTypeNameForReference', bezeichnung: 'Familie und Typ als Referenz', art: 'ja_nein' },
      ],
    },
    {
      titel: 'Umfang der Geometrie',
      felder: [
        { schluessel: 'visibleElementsOfCurrentViewOnly', bezeichnung: 'Nur sichtbare Elemente', art: 'ja_nein' },
        { schluessel: 'exportLinkedFiles', bezeichnung: 'Verknüpfte Modelle', art: 'ja_nein' },
        { schluessel: 'splitWallsAndColumnsByLevel', bezeichnung: 'Wände und Stützen je Geschoss teilen', art: 'ja_nein' },
        { schluessel: 'exportPartsAsBuildingElements', bezeichnung: 'Teile als Bauteile', art: 'ja_nein' },
        { schluessel: 'exportRoomsInView', bezeichnung: 'Räume der Ansicht', art: 'ja_nein' },
        { schluessel: 'export2dElements', bezeichnung: 'Zweidimensionale Elemente', art: 'ja_nein' },
        { schluessel: 'includeSteelElements', bezeichnung: 'Stahlbauelemente', art: 'ja_nein' },
        { schluessel: 'exportBoundingBox', bezeichnung: 'Hüllquader', art: 'ja_nein' },
        { schluessel: 'tessellationLevelOfDetail', bezeichnung: 'Feinheit der Facettierung', art: 'kommazahl',
          hinweis: '0 bis 1. Höher heißt genauer und größer.' },
        { schluessel: 'useOnlyTriangulation', bezeichnung: 'Nur Dreiecke', art: 'ja_nein' },
        { schluessel: 'use2dRoomBoundaryForVolume', bezeichnung: 'Raumvolumen aus 2D-Grenzen', art: 'ja_nein' },
      ],
    },
    {
      titel: 'Ablauf',
      felder: [
        { schluessel: 'namingPattern', bezeichnung: 'Dateiname', art: 'text' },
      ],
    },
  ],
};

// Die Leseläufe stehen im Profilkopf, nicht in einem Formatabschnitt. Sie sind
// keine Ausgabe, sondern Arbeit am Modell — Lesen und Zurückschreiben.
ABSCHNITTE.profile = [
  {
    titel: 'Aus dem Modell lesen',
    felder: [
      { schluessel: 'raumaufnahme', bezeichnung: 'Raumbuch lesen', art: 'ja_nein' },
      { schluessel: 'tueraufnahme', bezeichnung: 'Türbuch lesen', art: 'ja_nein' },
      { schluessel: 'lueftungsaufnahme', bezeichnung: 'Lüftung lesen', art: 'ja_nein' },
      { schluessel: 'heizungsaufnahme', bezeichnung: 'Heizung und Kälte lesen',
        art: 'ja_nein', wichtig: true,
        hinweis: 'Liest Geräte, Armaturen und Rohrnetze. Die Anschlusspunkte kommen '
               + 'mit — sie tragen die technischen Werte. Wo kein Raumparameter '
               + 'gepflegt ist, entsteht die Zuordnung aus der Lage.' },
      { schluessel: 'elektroaufnahme', bezeichnung: 'Elektro lesen',
        art: 'ja_nein', wichtig: true,
        hinweis: 'Liest neun Revit-Kategorien: Leuchten, Installationsgeräte, '
               + 'Betriebsmittel, Brandmeldeanlagen, Sicherheitstechnik, '
               + 'Elektroakustik, Datentechnik, Notruf, Lichtquellen. Rund 32.000 '
               + 'Elemente, davon 28.600 geführt. Die Zuordnung zu Verteilkreisen '
               + 'entsteht danach über den Raum und dessen Verteilerbereich im '
               + 'Raumbuch — im Modell ist nichts verschaltet.' },
      { schluessel: 'planaufnahme', bezeichnung: 'Planköpfe und Indexstände lesen',
        art: 'ja_nein' },
    ],
  },
  {
    titel: 'Ins Modell zurückschreiben',
    felder: [
      { schluessel: 'raumrueckgabe', bezeichnung: 'Raumbuch zurückschreiben', art: 'ja_nein' },
      { schluessel: 'tuerrueckgabe', bezeichnung: 'Türbuch zurückschreiben', art: 'ja_nein' },
      { schluessel: 'lueftungsrueckgabe', bezeichnung: 'Lüftung zurückschreiben', art: 'ja_nein' },
      { schluessel: 'heizungsrueckgabe', bezeichnung: 'Heizung zurückschreiben',
        art: 'ja_nein',
        hinweis: 'Noch nicht gebaut — der Schalter ist vorhanden, der Lauf tut nichts.' },
      { schluessel: 'elektrorueckgabe', bezeichnung: 'Elektro zurückschreiben',
        art: 'ja_nein', wichtig: true,
        hinweis: 'Schreibt Raumnummer, Raumname, Quadrant, Verteiler, Stromkreis und '
               + 'die gepflegten Werte ins Modell. Fehlende TuV_ELT_-Parameter werden '
               + 'angelegt und an alle neun Elektrokategorien gebunden. Die '
               + 'Raumzuordnung stammt aus der Lage — sie berichtigt die '
               + 'Modellparameter, von denen nur 46 von 665 zum Raumbuch passten.' },
      { schluessel: 'planrueckgabe', bezeichnung: 'Planstände zurückschreiben', art: 'ja_nein' },
    ],
  },
  {
    titel: 'Allgemein',
    felder: [
      { schluessel: 'name', bezeichnung: 'Name des Profils', art: 'text' },
      { schluessel: 'revitVersion', bezeichnung: 'Revit-Fassung', art: 'zahl' },
      { schluessel: 'loadUnloadedLinks', bezeichnung: 'Nicht geladene Verknüpfungen laden',
        art: 'ja_nein',
        hinweis: 'Nicht geladene Verknüpfungen erscheinen NICHT im Export. Das Laden '
               + 'kostet Zeit, aber ein unvollständiger Plan kostet mehr.' },
    ],
  },
];

const KOPFTEXT = 'Was ein Auftrag exportiert und wie. Eine bestehende Fassung wird nie '
  + 'geändert — Speichern legt eine neue an, damit ein Auftrag von vor Monaten '
  + 'nachvollziehbar bleibt.';

const FORMATE = [
  { schluessel: 'profile', name: 'Leseläufe' },
  { schluessel: 'pdf', name: 'PDF' },
  { schluessel: 'dwg', name: 'DWG' },
  { schluessel: 'ifc', name: 'IFC' },
];

// --- Die Seite ---------------------------------------------------------------

interface Rahmen { api: ApiClient; projekt?: { id: string } }

export function Profile() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [profile, setProfile] = useState<Profil[] | null>(null);
  const [fassungId, setFassungId] = useState<string | null>(null);
  const [fassung, setFassung] = useState<Profilfassung | null>(null);
  const [entwurf, setEntwurf] = useState<Record<string, unknown> | null>(null);
  const [neueVersion, setNeueVersion] = useState('');
  const [format, setFormat] = useState('dwg');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = async () => {
    try {
      const p = await api.profileAlle(projekt?.id);
      setProfile(p);

      if (!fassungId && p.length > 0 && p[0].versions.length > 0)
        setFassungId(p[0].versions[0].id);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projekt?.id]);

  useEffect(() => {
    if (!fassungId) return;

    (async () => {
      try {
        const f = await api.profilfassung(fassungId);
        setFassung(f);
        setEntwurf(JSON.parse(JSON.stringify(f.settings)));
        setNeueVersion(naechsteVersion(f.version));
        setMeldung(null);
      } catch (e) {
        setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [api, fassungId]);

  if (fehler) {
    return (
      <>
        <PageHeader titel="Exportprofile" beschreibung={KOPFTEXT} />
        <ErrorState
          meldung="Die Exportprofile konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <PageHeader titel="Exportprofile" beschreibung={KOPFTEXT} />
        <LoadingState text="Exportprofile werden geladen …" zeilen={8} spalten={2} />
      </>
    );
  }

  const profil = profile.find((p) => p.versions.some((v) => v.id === fassungId));
  const teil = (entwurf?.[format] ?? {}) as Record<string, unknown>;

  const setzen = (schluessel: string, wert: unknown) => {
    if (!entwurf) return;

    setEntwurf({
      ...entwurf,
      [format]: { ...(entwurf[format] as object), [schluessel]: wert },
    });
  };

  const geaendert = fassung !== null && entwurf !== null
    && JSON.stringify(fassung.settings) !== JSON.stringify(entwurf);

  const speichern = async () => {
    if (!profil || !entwurf) return;

    setLaeuft(true);
    setMeldung(null);
    try {
      // Die Versionsnummer in die Definition schreiben — der Server liest sie von
      // dort und legt die Fassung darunter an.
      const definition = {
        ...entwurf,
        profile: { ...(entwurf.profile as object), version: neueVersion },
      };

      const a = await api.profilfassungAnlegen(profil.id, definition);

      setMeldung(`Fassung ${neueVersion} angelegt. Sie ist noch ein Entwurf — `
               + 'zum Ausführen muss sie freigegeben werden.');

      await laden();
      if (a && 'id' in a) setFassungId(a.id);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (profile.length === 0) {
    return (
      <>
        <PageHeader titel="Exportprofile" beschreibung={KOPFTEXT} />
        <EmptyState
          titel="Noch kein Exportprofil angelegt."
          erklaerung={'Ohne Profil weiß ein Auftrag nicht, was er exportieren soll. '
            + 'Profile werden über die Verwaltung angelegt.'}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        titel="Exportprofile"
        beschreibung={KOPFTEXT}
        meta={fassung
          ? `Fassung ${fassung.version} · Revit ${fassung.revitVersion}`
          : `${profile.length} Profil(e)`}
        aktionen={fassung?.isReleased
          ? <StatusBadge zustand="gut" text="freigegeben" />
          : undefined}
      />

      <ContentSection titel="Fassung" beschreibung="Profil und Fassung wählen.">
      {/* Die Fassungswahl */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={fassungId ?? ''}
          onChange={(e) => setFassungId(e.target.value)}
          style={{ fontSize: 13, minWidth: 340 }}
        >
          {profile.map((p) => (
            <optgroup key={p.id} label={p.name}>
              {p.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.version} · Revit {v.revitVersion}
                  {v.isReleased ? ' · freigegeben' : ' · Entwurf'}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {fassung && !fassung.isReleased && (
          <button
            disabled={laeuft}
            onClick={async () => {
              setLaeuft(true);
              try {
                await api.profilfassungFreigeben(fassung.id);
                setMeldung('Freigegeben. Neue Aufträge nutzen jetzt diese Fassung.');
                await laden();
                const f = await api.profilfassung(fassung.id);
                setFassung(f);
              } catch (e) {
                setMeldung(e instanceof Error ? e.message : String(e));
              } finally { setLaeuft(false); }
            }}
            className="m-knopf m-knopf-haupt"
            title="Erst eine freigegebene Fassung wird von Aufträgen verwendet"
          >
            Freigeben
          </button>
        )}

        {fassung?.isReleased && (
          <StatusBadge klein zustand="gut" text="freigegeben" />
        )}
      </div>

      {meldung && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <IconOk size={13} /> {meldung}
        </div>
      )}

      {!entwurf ? (
        <LoadingState text="Fassung wird geladen …" zeilen={6} spalten={3} />
      ) : (
        <>
          {/* Die Formatwahl */}
          <div style={{ display: 'flex', gap: 6, marginTop: 18, flexWrap: 'wrap' }}>
            {FORMATE.map((f) => {
              const teilF = (entwurf[f.schluessel] ?? {}) as Record<string, unknown>;

              return (
                <button
                  key={f.schluessel}
                  onClick={() => setFormat(f.schluessel)}
                  className={format === f.schluessel ? 'betont' : undefined}
                >
                  {f.name}
                  {f.schluessel === 'profile'
                    ? Object.entries(teilF).some(([k, v]) =>
                        v === true && k !== 'loadUnloadedLinks') && (
                        <span className="marke gut" style={{ marginLeft: 6 }}>aktiv</span>
                      )
                    : teilF.enabled === true && (
                        <span className="marke gut" style={{ marginLeft: 6 }}>aktiv</span>
                      )}
                </button>
              );
            })}
          </div>

          {/* Der Schalter für das ganze Format.

              Nicht bei den Leseläufen: Dort ist jeder Lauf einzeln schaltbar, und
              ein Sammelschalter darüber gibt es im Profilkopf nicht. */}
          {format !== 'profile' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                            marginTop: 14, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={teil.enabled === true}
                onChange={(e) => setzen('enabled', e.target.checked)}
              />
              {FORMATE.find((f) => f.schluessel === format)?.name} in diesem Profil
              exportieren
            </label>
          )}

          {(ABSCHNITTE[format] ?? []).map((a) => (
            <div key={a.titel} style={{ marginTop: 20 }}>
              <div className="feldname" style={{ fontSize: 13 }}>{a.titel}</div>

              {a.felder.map((f) => (
                <Feldzeile
                  key={f.schluessel}
                  feld={f}
                  wert={teil[f.schluessel]}
                  aendern={(w) => setzen(f.schluessel, w)}
                />
              ))}
            </div>
          ))}

          {/* Speichern */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24, alignItems: 'center',
                        flexWrap: 'wrap', borderTop: '1px solid var(--linie, #ddd)',
                        paddingTop: 16 }}>
            <div>
              <div className="feldname">Neue Fassungsnummer</div>
              <input
                value={neueVersion}
                onChange={(e) => setNeueVersion(e.target.value)}
                style={{ width: 120, fontSize: 13 }}
              />
            </div>

            <button
              className={geaendert ? 'betont' : undefined}
              disabled={laeuft || !geaendert || !neueVersion.trim()}
              onClick={speichern}
            >
              {laeuft ? 'wird angelegt …' : 'Als neue Fassung speichern'}
            </button>

            {geaendert ? (
              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                Änderungen vorhanden. Die Fassung {fassung?.version} bleibt
                unverändert bestehen.
              </span>
            ) : (
              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                Keine Änderung gegenüber {fassung?.version}.
              </span>
            )}
          </div>
        </>
      )}
      </ContentSection>
    </>
  );
}

// --- Eine Feldzeile ----------------------------------------------------------

function Feldzeile({
  feld: f, wert, aendern,
}: {
  feld: Feld;
  wert: unknown;
  aendern: (wert: unknown) => void;
}) {
  const [hinweisOffen, setHinweisOffen] = useState(false);

  return (
    <div className={f.wichtig ? 'modellfeld' : undefined}
         style={{ marginBottom: 5, padding: f.wichtig ? undefined : '2px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 250, fontSize: 13 }}>
          {f.bezeichnung}
          <div className="sehr-schwach" style={{ fontSize: 10 }}>
            <code>{f.schluessel}</code>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 240 }}>
          {f.art === 'ja_nein' ? (
            <input
              type="checkbox"
              checked={wert === true}
              onChange={(e) => aendern(e.target.checked)}
            />
          ) : f.art === 'auswahl' ? (
            <select
              value={typeof wert === 'string' ? wert : ''}
              onChange={(e) => aendern(e.target.value)}
              style={{ width: '100%', fontSize: 13 }}
            >
              <option value="">— nicht gesetzt —</option>
              {(f.auswahl ?? []).map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
              {/* Ein Bestandswert außerhalb der Liste muss sichtbar bleiben —
                  sonst sähe das Feld leer aus, obwohl etwas drinsteht. */}
              {typeof wert === 'string' && wert && !(f.auswahl ?? []).includes(wert) && (
                <option value={wert}>{wert} (nicht in der Liste)</option>
              )}
            </select>
          ) : f.art === 'liste' ? (
            <input
              value={Array.isArray(wert) ? wert.join(', ') : ''}
              onChange={(e) => {
                const teile = e.target.value.split(',').map((x) => x.trim())
                  .filter((x) => x.length > 0);
                aendern(teile.length > 0 ? teile : null);
              }}
              placeholder="leer = alle"
              style={{ width: '100%', fontSize: 13 }}
            />
          ) : (
            <input
              value={wert === null || wert === undefined ? '' : String(wert)}
              onChange={(e) => {
                const t = e.target.value;

                if (f.art === 'zahl') {
                  const n = parseInt(t, 10);
                  aendern(Number.isNaN(n) ? 0 : n);
                } else if (f.art === 'kommazahl') {
                  const n = parseFloat(t.replace(',', '.'));
                  aendern(Number.isNaN(n) ? 0 : n);
                } else {
                  aendern(t === '' ? null : t);
                }
              }}
              type={f.art === 'zahl' || f.art === 'kommazahl' ? 'number' : 'text'}
              step={f.art === 'kommazahl' ? '0.1' : undefined}
              style={{ width: '100%', fontSize: 13 }}
            />
          )}
        </div>

        {f.hinweis && (
          <button
            onClick={() => setHinweisOffen(!hinweisOffen)}
            style={{ padding: '2px 8px', fontSize: 11 }}
            title="Hinweis"
          >
            {hinweisOffen ? '−' : 'i'}
          </button>
        )}
      </div>

      {hinweisOffen && f.hinweis && (
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 4,
                                               maxWidth: 700 }}>
          {f.hinweis}
        </div>
      )}
    </div>
  );
}

/**
 * Die nächste Fassungsnummer vorschlagen.
 *
 * Aus 2.0.0 wird 2.0.1. Kein Ratespiel um die Bedeutung der Stellen — wer eine
 * größere Änderung macht, überschreibt den Vorschlag.
 */
function naechsteVersion(aktuell: string): string {
  const teile = aktuell.split('.');

  if (teile.length !== 3) return aktuell + '.1';

  const letzte = parseInt(teile[2], 10);

  return Number.isNaN(letzte)
    ? aktuell + '.1'
    : `${teile[0]}.${teile[1]}.${letzte + 1}`;
}
